import { useTranslation } from 'next-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { useRef } from 'react'

import { selectPoints } from '~/gamification/slices/gamification.slice'
import useCalculateLevel from '~/hooks/useCalculateLevel'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectIsServerErrorInNicknameTab, setFieldValueActionCreator } from '~/settings-scenario/redux/settings.slice'
import { UserField } from '~/types/user'
import userSettingsAnalytics from '~/analytics/userSettings/userSettingsAnalytics'

import ServerError from '~/settings-scenario/components/ServerError'
import { useUpdateSettings } from '~/settings-scenario/hooks'
import { Tab } from '~/settings-scenario/types'
import SVGImage from '~/components/Image/SVGImage'
import NickNameField from '~/components/NickNameField'
import { useGetRewardsQuery } from '~/redux/apis/reward-api'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import getUserTitle from '~/components/UserLevel/UserLevel.utils'

export default function GamificationUserSection(): React.ReactElement {
  const { t } = useTranslation(['fields', 'homebase', 'dashboard'])
  const user = useCurrentUser()
  const points = useAppSelector(selectPoints)
  const userLevelData = useCalculateLevel(points ? points : 0)
  const xpPercentToNextLvl = Math.floor((userLevelData.xpGainedThisLvl / userLevelData.xpToNextLvl) * 100)
  const value = useRef('')
  const dispatch = useAppDispatch()
  const isServerErrorInNicknameTab = useAppSelector(selectIsServerErrorInNicknameTab)
  const handleOnSubmit = useUpdateSettings()
  const authenticated = useAppSelector(isAuthenticated)

  const { data: rewardsData } = useGetRewardsQuery(undefined, {
    skip: !authenticated,
    refetchOnMountOrArgChange: 10,
  })

  const logNickNameChange = useDebouncedCallback(() => userSettingsAnalytics.writingInForms('nickname'), 500, {
    leading: true,
    trailing: false,
  })

  const handleOnInputChange = (name: string): void => {
    value.current = name

    dispatch(setFieldValueActionCreator({ fieldName: UserField.NICKNAME, value: name }))

    logNickNameChange()
  }

  const handleSaving = async (): Promise<void> => {
    if (value.current.trim().length !== 0) await handleOnSubmit(Tab.AVATAR_NICKNAME)
  }

  const { xpGainedThisLvl, xpToNextLvl } = userLevelData
  const nameLengthToShow = 12
  const userTitle = getUserTitle(userLevelData.lvl)

  return (
    <div className="relative md:w-full flex flex-col items-center gap-2 px-3 lg:px-4">
      <div className="flex justify-center items-center gap-2">
        {user?.nickname ? (
          <h1 className="font-extrabold md:text-center text-5 leading-6 whitespace-nowrap">
            {user.nickname.length > nameLengthToShow
              ? `${user.nickname.slice(0, nameLengthToShow - 2)}...`
              : user.nickname}
          </h1>
        ) : (
          <div className="w-full text-xs">
            <NickNameField
              onBlur={handleSaving}
              onChange={handleOnInputChange}
              placeHolder={t('fields:addNickname')}
              value={value.current}
            />
          </div>
        )}
        <div className="flex items-center bg-white-whisper px-2 py-1 border border-white-veil rounded-full">
          <p className="font-bold text-2.75 whitespace-nowrap">
            {t('dashboard:level')} {userLevelData?.lvl}
          </p>
        </div>
      </div>
      {isServerErrorInNicknameTab && <ServerError />}
      <div className="flex items-center bg-white-whisper px-2.5 py-1 border border-white-veil rounded-full">
        <p className="font-medium text-gray-200 text-2.75 whitespace-nowrap">{t(`homebase:${userTitle}`)}</p>
      </div>
      <div className="flex flex-col gap-2 bg-white-mist p-2 rounded-3 w-full">
        <div className="flex items-center gap-2">
          <SVGImage alt="xp" height={18} imageClassName="w-4.5" srcKey="gamification/xp.svg" width={18} />
          <p className="font-semibold text-sm lg:text-xs">
            {t('dashboard:totalXP')}: {rewardsData?.xp.toLocaleString()}
          </p>
        </div>
        <div className="flex flex-col gap-1.5 w-full">
          <div className="flex justify-between text-xxs">
            <span className="text-gray-200">{t('dashboard:progressToNextLevel')}</span>
            <span className="font-bold">{xpToNextLvl ? Math.floor((xpGainedThisLvl / xpToNextLvl) * 100) : '0'}%</span>
          </div>
          <div className="bg-white-whisper rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-gradient-purple-violet-right h-full rounded-full"
              style={{ width: `${xpPercentToNextLvl}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
